/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import classnames from "classnames";
import snakeCase from "lodash/snakeCase";
import Radio from "components/shared/Radio";
import Input from "components/shared/Input";
import LcFetch from "lib/shared/fetch";

const validationSchema = Yup.object().shape({
  cancellationReason: Yup.string().required("Please select a reason"),
  didntFindDescription: Yup.string().when("cancellationReason", {
    is: (cancellationReason) => cancellationReason === "didnt_find",
    otherwise: () => Yup.string().nullable(),
    then: () => Yup.string().required("Field is required")
  }),
  otherDescription: Yup.string().when("cancellationReason", {
    is: (cancellationReason) => cancellationReason === "other",
    otherwise: () => Yup.string().nullable(),
    then: () => Yup.string().required("Field is required")
  }),
  technicalIssueDescription: Yup.string().when("cancellationReason", {
    is: (cancellationReason) => cancellationReason === "technical_issue",
    otherwise: () => Yup.string().nullable(),
    then: () => Yup.string().required("Field is required")
  })
});

const onRadioChange = (e, formik) => {
  formik.setValues({
    cancellationReason: e.target.value,
    didntFindDescription: "",
    otherDescription: "",
    technicalIssueDescription: ""
  });
};

const handleSubmit =
  ({ setFormError, setStepData }) =>
  async (values) => {
    setFormError(null);
    const params = Object.entries(values).reduce((acc, [key, value]) => {
      if (value) {
        acc[snakeCase(key)] = value;
      }
      return acc;
    }, {});
    try {
      const response = await LcFetch("/account/cancel", {
        body: JSON.stringify({ user_cancellation_form: params }),
        method: "POST"
      });
      if (response.ok) {
        const data = await response.json();
        setStepData({ confirmation: data.confirmation, step: 2 });
      } else if (response.status === 422) {
        const data = await response.json();
        setFormError(data.error);
      }
    } catch (_error) {
      setFormError(
        "There was an error processing your request. Please try again, and refresh the page if the issue persists."
      );
    }
  };

export default function UnsubscribeModal(props) {
  const { pastDueSubscription, subscriptionEndAt } = props;
  const [stepData, setStepData] = useState({ step: 1 });
  const [formError, setFormError] = useState(null);
  const formik = useFormik({
    initialValues: {
      cancellationReason: "",
      didntFindDescription: "",
      otherDescription: "",
      technicalIssueDescription: ""
    },
    onSubmit: handleSubmit({ setFormError, setStepData }),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema
  });
  useEffect(() => {
    const resetForm = () => {
      formik.resetForm();
      setStepData({ step: 1 });
    };
    $(".unsubscribe-modal.modal").on("hidden.bs.modal", resetForm);
    return () => {
      $(".unsubscribe-modal.modal").off("hidden.bs.modal", resetForm);
    };
  }, []);
  if (stepData.step === 1) {
    return (
      <CancellationForm
        pastDueSubscription={pastDueSubscription}
        subscriptionEndAt={subscriptionEndAt}
        formik={formik}
        formError={formError}
      />
    );
  }
  return <CancellationConfirmation stepData={stepData} />;
}

function CancellationForm(props) {
  const { pastDueSubscription, subscriptionEndAt, formik, formError } = props;
  return (
    <>
      <div className="modal-body">
        <div className="step-1">
          <p className="unsubscribe-modal__body-top">
            <CancellationNotice
              pastDueSubscription={pastDueSubscription}
              subscriptionEndAt={subscriptionEndAt}
            />{" "}
            LitCharts will not charge you again unless you reactivate your
            subscription.
          </p>
          <div className="cancellation-form">
            {formError && (
              <div
                className="cancellation-form-errors error"
                style={{ display: "block" }}
              >
                {formError}
              </div>
            )}
            {formik.errors.cancellationReason && (
              <div
                className="blank-selection-error error"
                style={{ display: "block" }}
              >
                Please select a reason
              </div>
            )}
            <form onSubmit={formik.handleSubmit}>
              <div className="radio-left">
                <div className="cancellation-reason">
                  <Radio
                    name="user_cancellation_form[cancellation_reason]"
                    value="technical_issue"
                    label="I am having a technical issue."
                    inputData={{
                      "error-id": "technical-issue-error",
                      "needs-validation": true,
                      "textarea-id": "technical-issue-textarea"
                    }}
                    onChange={(e) => onRadioChange(e, formik)}
                    checked={
                      formik.values.cancellationReason === "technical_issue"
                    }
                  />
                  <div
                    className={classnames("cancellation-description", {
                      selected:
                        formik.values.cancellationReason === "technical_issue"
                    })}
                  >
                    <label htmlFor="technical-issue-textarea">
                      Please tell us about the problem you encountered.
                    </label>
                    <Input
                      isControlled
                      InputComponent="textarea"
                      name="user_cancellation_form[technical_issue_description]"
                      inputId="technical-issue-textarea"
                      value={formik.values.technicalIssueDescription}
                      handleChange={(e) =>
                        formik.setFieldValue(
                          "technicalIssueDescription",
                          e.target.value
                        )
                      }
                    />
                    {formik.errors.technicalIssueDescription && (
                      <div
                        id="technical-issue-error"
                        className="error"
                        style={{ display: "block" }}
                      >
                        A response is required.
                      </div>
                    )}
                  </div>
                </div>
                <div className="cancellation-reason">
                  <Radio
                    name="user_cancellation_form[cancellation_reason]"
                    value="too_expensive"
                    label="It's too expensive."
                    onChange={(e) => onRadioChange(e, formik)}
                    checked={
                      formik.values.cancellationReason === "too_expensive"
                    }
                  />
                </div>
                <div className="cancellation-reason">
                  <Radio
                    name="user_cancellation_form[cancellation_reason]"
                    value="didnt_find"
                    label="I didn't find the help I needed."
                    onChange={(e) => onRadioChange(e, formik)}
                    checked={formik.values.cancellationReason === "didnt_find"}
                  />
                  <div
                    className={classnames("cancellation-description", {
                      selected:
                        formik.values.cancellationReason === "didnt_find"
                    })}
                  >
                    <label htmlFor="didnt-find-textarea">
                      Which resources were you unable to find?
                    </label>
                    <Input
                      isControlled
                      InputComponent="textarea"
                      name="user_cancellation_form[didnt_find_description]"
                      inputId="didnt-find-textarea"
                      value={formik.values.didntFindDescription}
                      handleChange={(e) =>
                        formik.setFieldValue(
                          "didntFindDescription",
                          e.target.value
                        )
                      }
                    />
                    {formik.errors.didntFindDescription && (
                      <div
                        id="didnt-find-error"
                        className="error"
                        style={{ display: "block" }}
                      >
                        A response is required.
                      </div>
                    )}
                  </div>
                </div>
                <div className="cancellation-reason">
                  <Radio
                    name="user_cancellation_form[cancellation_reason]"
                    value="dont_need"
                    label="I just don't need it anymore."
                    onChange={(e) => onRadioChange(e, formik)}
                    checked={formik.values.cancellationReason === "dont_need"}
                  />
                </div>
                <div className="cancellation-reason">
                  <Radio
                    name="user_cancellation_form[cancellation_reason]"
                    value="prefer_not_to_answer"
                    label="I would prefer not to answer."
                    onChange={(e) => onRadioChange(e, formik)}
                    checked={
                      formik.values.cancellationReason ===
                      "prefer_not_to_answer"
                    }
                  />
                </div>
                <div className="cancellation-reason">
                  <Radio
                    name="user_cancellation_form[cancellation_reason]"
                    value="other"
                    label="Some other reason."
                    onChange={(e) => onRadioChange(e, formik)}
                    checked={formik.values.cancellationReason === "other"}
                  />
                </div>
                <div
                  className={classnames("cancellation-description", {
                    selected: formik.values.cancellationReason === "other"
                  })}
                >
                  <label htmlFor="other-textarea">
                    Please explain briefly why you&apos;d like to cancel.
                  </label>
                  <Input
                    isControlled
                    InputComponent="textarea"
                    name="user_cancellation_form[other_description]"
                    inputId="other-textarea"
                    value={formik.values.otherDescription}
                    handleChange={(e) =>
                      formik.setFieldValue("otherDescription", e.target.value)
                    }
                  />
                  {formik.errors.otherDescription && (
                    <div
                      id="other-error"
                      className="error"
                      style={{ display: "block" }}
                    >
                      A response is required.
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <div className="step-1">
          <div className="unsubscribe-modal__buttons unsubscribe-modal__buttons--flex-end tw-hidden sm:tw-flex">
            <button
              className="btn btn-md btn-default unsubscribe-modal__close"
              type="button"
              data-dismiss="modal"
            >
              Don&apos;t Cancel
            </button>
            <button
              className="btn btn-md btn-primary unsubscribe-modal__submit"
              onClick={formik.submitForm}
              type="button"
            >
              Confirm Cancellation
            </button>
          </div>
          <div className="unsubscribe-modal__buttons sm:tw-hidden">
            <button
              className="btn btn-md btn-primary unsubscribe-modal__submit"
              type="button"
              onClick={formik.submitForm}
            >
              Confirm Cancellation
            </button>
            <button
              className="btn btn-md btn-default unsubscribe-modal__close"
              type="button"
              data-dismiss="modal"
            >
              Don&apos;t Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function CancellationNotice(props) {
  const { pastDueSubscription, subscriptionEndAt } = props;
  if (pastDueSubscription) {
    return <span>Please complete the information below to cancel.</span>;
  }
  return (
    <span>
      Your account will remain active until {subscriptionEndAt} at which time
      you will no longer have access to any LitCharts materials that require a
      subscription.
    </span>
  );
}

function CancellationConfirmation({ stepData }) {
  return (
    <div className="modal-body">
      <div className="step-confirmed">
        <h2 className="modal-title text-left">Cancellation Confirmed</h2>
        <p className="unsubscribe-modal__body-top">
          Your cancellation confirmation number is{" "}
          <span className="unsubscribe-modal__confirmation-number">
            {stepData.confirmation}
          </span>
        </p>
        <div className="quote">
          <p className="text">
            <i className="lc-icon lc-icon-quote-filled flip-x" />
            Parting is such sweet sorrow that I shall say [your subscription has
            been canceled].
            <i className="lc-icon lc-icon-quote-filled flip-y" />
          </p>
          <p className="citation">
            —{" "}
            <a href="/shakescleare/shakespeare-translations/romeo-and-juliet/">
              Romeo and Juliet
            </a>
            ,{" "}
            <a href="/shakescleare/shakespeare-translations/romeo-and-juliet/act-2-scene-2">
              {" "}
              Act 2 Scene 2
            </a>
          </p>
        </div>
      </div>
      <div className="modal-footer">
        <div className="step-confirmed">
          <button
            className="btn btn-md btn-primary"
            type="button"
            data-dismiss="modal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
